import { useEffect, useState, useRef } from "react";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Layout } from "@components/Layout/index";
import { ContentSection } from "@components/ContentSection";
import {
  getAllDocumentsByType,
  getProduct,
  getProductSettings,
  getModalSettings,
  getGlobalConfig,
  getBundle,
} from "@lib/api/calls";
import { trackEvent } from "@lib/track-event";
import { BundleView, Reviews, SingleView } from "@components/Product";
import { Redirect } from "@components/Redirect";
import { Render } from "@components/sections";
import * as junipService from "@services/junip";
import { useMobileDetect } from "@hooks/useMobileDetect";
import { ReviewsSection } from "@components/Product/Reviews/ReviewsSection";
import { getProductGroupsFromRedis } from "@lib/redis";

export default function Slug({
  preview,
  title = "Products",
  seo,
  product,
  productSettings = null,
  shopifyProductID,
  junipReviews,
  junipProduct,
  config,
  subscribed,
  modal,
}) {
  const [showReviews, setShowReviews] = useState(false);
  const [targetElement, setTargetElement] = useState(null);
  const reviewsRef = useRef(null);
  const reviewsSectionRef = useRef(null);
  const currentDevice = useMobileDetect();

  const scrollToReviews = () => {
    let topPosition = 0;
    if (currentDevice.isMobile()) {
      topPosition = reviewsSectionRef.current.getBoundingClientRect().top + window.scrollY - 500;
    } else {
      topPosition = reviewsSectionRef.current.getBoundingClientRect().top + window.scrollY - 400;
    }
    window.scrollTo({ top: topPosition, behavior: "smooth" });
  };

  // Prevent automatic scrolling on navigation
  useEffect(() => {
    const handleNavigation = () => {
      if ("scrollRestoration" in history) {
        history.scrollRestoration = "manual";
      }
    };

    window.addEventListener("popstate", handleNavigation);

    return () => {
      window.removeEventListener("popstate", handleNavigation);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setTargetElement(reviewsRef.current);
    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  useEffect(() => {
    trackEvent("view_page", {
      pageTitle: title,
      pageCategory: product && product.type === "bundle" ? "Bundle Page" : "Product Page",
      pageLocation: window.location.href,
      pagePath: window.location.pathname,
      noFire: "pinterest",
    });
  }, [title, product]);

  function closeReviewsDrawer() {
    setShowReviews(false);
    if (currentDevice.isMobile()) {
      enableBodyScroll(targetElement);
    }
  }

  function openReviewsDrawer() {
    setShowReviews(true);
    if (currentDevice.isMobile()) {
      disableBodyScroll(targetElement);
    }
  }

  if (product && product.type === "bundle") {
    return (
      <Layout
        config={config}
        seo={seo}
        title={title}
        subscribed={subscribed}
        pageFound={product !== null}
        modal={modal}
        isPreview={preview}
      >
        <BundleView bundle={product} />
      </Layout>
    );
  }

  if (product && product.deadstock) {
    return (
      <Layout
        config={config}
        seo={seo}
        title={title}
        subscribed={subscribed}
        pageFound={product !== null}
        modal={modal}
        isPreview={preview}
      >
        <Redirect type="product" to={product.deadstock.to} label={product.deadstock.toLabel} />
      </Layout>
    );
  }
  return (
    <Layout
      config={config}
      title={title}
      seo={seo}
      subscribed={subscribed}
      pageFound={product !== null}
      modal={modal}
      isPreview={preview}
    >
      {product && product.shopifyProducts && product.shopifyProducts.length && (
        <SingleView
          product={product}
          settings={productSettings}
          openReviewsDrawer={openReviewsDrawer}
          scrollToReviews={scrollToReviews}
        />
      )}
      {product && product.shopifyProducts && product.shopifyProducts.length && (
        <ReviewsSection
          shopifyProductID={shopifyProductID}
          showReviews={showReviews}
          staticReviews={junipReviews}
          staticProduct={junipProduct}
          closeReviewsDrawer={closeReviewsDrawer}
          ref={reviewsSectionRef}
        />
      )}

      {product && product.blocks && <Render sections={product.blocks} />}

      {product && product.contentSectionsRef?.length > 0 && (
        <>
          {product.contentSectionsRef.map((section) => (
            <ContentSection key={section._id} section={section} />
          ))}
        </>
      )}

      {product && product.shopifyProducts && product.shopifyProducts.length && (
        <Reviews
          shopifyProductID={shopifyProductID}
          showReviews={showReviews}
          staticReviews={junipReviews}
          staticProduct={junipProduct}
          closeReviewsDrawer={closeReviewsDrawer}
          ref={reviewsRef}
        />
      )}
    </Layout>
  );
}

export async function getStaticPaths({ locales }) {
  const allProducts = await getAllDocumentsByType("productPage");
  const allBundles = await getAllDocumentsByType("bundle");
  const allPaths = allBundles.concat(allProducts) || [];
  const paths = [];

  allPaths.forEach((product) => {
    // Add default locale path (en)
    paths.push({
      params: {
        slug: product.slug,
      },
    });

    // Add paths for other locales
    locales.forEach((locale) => {
      if (locale !== "en") {
        paths.push({
          params: {
            slug: product.slug,
          },
          locale,
        });
      }
    });
  });

  return {
    paths,
    fallback: true,
  };
}

export async function getStaticProps({ params, preview = false, locale }) {
  //let productPage = await getProduct(params.slug, locale, preview);
  let productPage = await getProductGroupsFromRedis(params.slug);
  // if (!productPage) {
  //   productPage = await getBundle(params.slug, locale, preview);
  // }
  const settings = await getProductSettings(locale, preview);
  const modal = await getModalSettings(locale, preview);
  const config = await getGlobalConfig(locale, preview);

  let junipReviews = {};
  let junipProduct = null;
  let shopifyProduct = null;
  let shopifyId = null;
  if (productPage && productPage?.type !== "bundle") {
    try {
      shopifyProduct = productPage?.shopifyProducts?.find(
        (p) => p?.productReferenceV2?.store?.status === "active"
      );
      shopifyId = shopifyProduct?.productReferenceV2?.store?.id;
      junipProduct = await junipService?.junipProduct(shopifyId);

      if (junipProduct) {
        junipReviews = await junipService?.junipReviews(junipProduct.id);
      }
    } catch (error) {
      console.log(`[fetch-Junip] Error for product ${shopifyId}: ${error}`);
    }
  }

  const seo = {
    seoTitle: productPage?.seoTitle || `${productPage?.title} | Kotn`,
    seoDescription:
      productPage?.seoDescription ||
      (productPage?.description && productPage?.description[0].children[0].text) ||
      null,
    openGraphImage: productPage?.openGraphImage || null,
    twitterCard: productPage?.seoTwitterCardStyle || "summary_large_image",
  };

  return {
    props: {
      preview,
      title: productPage?.title || null,
      seo,
      product: productPage || null,
      productSettings: settings || null,
      shopifyProductID: shopifyProduct ? shopifyProduct?.productReferenceV2?.store?.id : null,
      junipReviews,
      junipProduct: junipProduct || null,
      modal,
      config,
      ...(await serverSideTranslations(locale, [
        "common",
        "navbar",
        "footer",
        "productpage",
        "sizeguide",
        "reviews",
        "cart",
        "bundles",
        "recommendations",
        "productcard",
        "style",
      ])),
    },
    revalidate: 1800,
  };
}
